import React, { useState, useEffect } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import "./style.scss";
import Select from "../../CoreComponent/Select";
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Card,
  CardContent,
  Box,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
} from "@mui/material";
import { styled } from "@mui/material/styles";

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:hover": {
    backgroundColor: "#fff3e0 !important",
    transform: "scale(1.01)",
    transition: "all 0.2s ease-in-out",
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const StyledCard = styled(Card)(({ theme }) => ({
  boxShadow: "0 8px 32px rgba(198, 40, 40, 0.1)",
  border: "1px solid #ffebee",
  borderRadius: "16px",
  transition: "all 0.3s ease",
  "&:hover": {
    boxShadow: "0 12px 40px rgba(198, 40, 40, 0.15)",
    transform: "translateY(-2px)",
  },
}));

const RoomPriceList = () => {
  const [allConference, setAllConference] = useState([]);
  const [conferenceId, setConferenceId] = useState("");
  const [roomPrices, setRoomPrices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // ✅ NEW STATES FOR EDIT MODAL
  const [editModal, setEditModal] = useState(false);
  const [editingHotel, setEditingHotel] = useState(null);
  const [roomId, setRoomId] = useState(null);
  const [editForm, setEditForm] = useState({
    conference_id: "",
    hotel_name: "",
    star_rating: "",
    single_base_price: "",
    double_base_price: "",
    triple_base_price: "",
  });

  const BaseUrl = process.env.REACT_APP_BASE_URL;
  const token = localStorage.getItem("token");
  const navigate = useNavigate();

  const starRatingConfig = {
    five_star: { label: "⭐⭐⭐⭐⭐ Five Star", color: "#ffd700", bg: "#fff3cd" },
    four_star: { label: "⭐⭐⭐⭐ Four Star", color: "#ffca28", bg: "#fff3e0" },
    three_star: { label: "⭐⭐⭐ Three Star", color: "#ff9800", bg: "#ffe0b2" },
  };

  // ✅ EDIT HANDLER
  const handleEdit = (hotel, roomId) => {
    setEditingHotel(hotel);
    setRoomId(roomId);
    setEditForm({
      conference_id: conferenceId.value,
      hotel_name: hotel.hotel_name,
      star_rating: hotel.star_rating,
      single_base_price: hotel.rooms?.Single?.base_price || "",
      double_base_price: hotel.rooms?.Double?.base_price || "",
      triple_base_price: hotel.rooms?.Triple?.base_price || "",
    });
    setEditModal(true);
  };

  // ✅ UPDATE API CALL
  const handleUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.put(
        `https://eventscons.com/eventbackendnew/api/room-prices/${roomId}`,
        editForm,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      toast.success("Hotel updated successfully!");
      setEditModal(false);
      fetchRoomPrices(); // Refresh table
      setEditForm({
        conference_id: "",
        hotel_name: "",
        star_rating: "",
        single_base_price: "",
        double_base_price: "",
        triple_base_price: "",
      });
    } catch (err) {
      toast.error(err.response?.data?.error || "Update failed!");
    } finally {
      setLoading(false);
    }
  };

  // ✅ DELETE HANDLER
  const handleDelete = async (roomId, hotelName) => {
    if (!window.confirm(`Delete "${hotelName}" room prices?`)) return;

    try {
      await axios.delete(`https://eventscons.com/eventbackendnew/api/room-prices/${roomId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Hotel deleted successfully!");
      fetchRoomPrices(); // Refresh table
    } catch (err) {
      toast.error("Delete failed!");
    }
  };

  // Fetch conferences
  const getConference = async () => {
    try {
      const response = await axios.get(`${BaseUrl}/conferences/all`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAllConference(
        response.data.data?.map((item) => ({
          label: item?.title,
          value: item?.id,
        })) || []
      );
    } catch (err) {
      console.error("Conference fetch error:", err);
      toast.error("Error loading conferences");
    }
  };

  // Fetch room prices from fixed API
  const fetchRoomPrices = async () => {
    if (!conferenceId?.value) {
      setRoomPrices([]);
      return;
    }

    setLoading(true);
    setError("");

    try {
      const url = `https://eventscons.com/eventbackendnew/api/room-prices/conference/${conferenceId.value}`;
      console.log("Calling:", url);

      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = response.data.data || response.data || [];

      // ✅ Modified grouping to include room IDs
      const groupedData = {};
      data.forEach((room) => {
        const key = `${room.hotel_name || "Unknown"}-${room.star_rating || "unknown"}`;
        if (!groupedData[key]) {
          groupedData[key] = {
            hotel_name: room.hotel_name || "Unknown Hotel",
            star_rating: room.star_rating || "unknown",
            room_id: room.id, // ✅ Store first room ID for edit/delete
            rooms: {},
          };
        }
        groupedData[key].rooms[room.room_type] = {
          base_price: parseFloat(room.base_price) || 0,
          companion_price: parseFloat(room.companion_price) || 0,
        };
      });

      setRoomPrices(Object.values(groupedData));
    } catch (err) {
      console.error("Room prices error:", err.response?.data || err.message);

      if (err.response?.status === 404) {
        setError("No room prices found for this conference.");
      } else if (err.response?.status === 401) {
        setError("Authentication failed. Please login again.");
      } else if (err.response?.status >= 500) {
        setError("Server error. Please try again later.");
      } else {
        setError(
          `Error: ${err.response?.data?.error || err.message || "Unknown error"}`
        );
      }

      toast.error("Failed to load room prices");
      setRoomPrices([]);
    } finally {
      setLoading(false);
    }
  };

  const handleConferenceChange = (value) => {
    setConferenceId(value);
    setRoomPrices([]);
    setError("");
  };

  useEffect(() => {
    getConference();
  }, []);

  useEffect(() => {
    fetchRoomPrices();
  }, [conferenceId]);

  return (
    <Box sx={{ p: 4, minWidth: 800, maxWidth: 1400, mx: "auto", width: "100%",height:'90%' }}>
      <StyledCard sx={{ mb: 4 }} style={{height:'90%'}}>
        <CardContent sx={{ p: [2, 4] }} style={{height:'90%'}}>
          <Typography
            variant="h4"
            sx={{
              color: "#c62828",
              textAlign: "center",
              mb: 3,
              fontWeight: 700,
              textShadow: "1px 1px 2px rgba(0,0,0,0.1)",
            }}
          >
            Room Prices Dashboard
          </Typography>

          {/* Conference Filter */}
          <Box sx={{ maxWidth: 400, mx: "auto", mb: 4 }}>
            <Select
              options={allConference}
              value={conferenceId}
              setValue={handleConferenceChange}
              label="Select Conference to View Room Prices"
              placeholder="Choose Conference..."
            />
          </Box>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 3, fontSize: "1.1rem" }}>
              {error}
            </Alert>
          )}

          {/* Loading */}
          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress size={60} sx={{ color: "#c62828" }} />
              <Typography sx={{ ml: 2, color: "#666" }}>
                Loading room prices...
              </Typography>
            </Box>
          )}

          {/* Empty State */}
          {!loading &&
            !error &&
            roomPrices.length === 0 &&
            conferenceId.value && (
              <Box sx={{ textAlign: "center", py: 8, color: "#999" }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  📭 No Room Prices Found
                </Typography>
                <Typography variant="body1">
                  No room prices available for this conference yet.
                </Typography>
              </Box>
            )}

          {/* Room Prices Table */}
          {!loading && roomPrices.length > 0 && (
            <TableContainer
              component={Paper}
              sx={{
                borderRadius: "12px",
                overflow: "hidden",
                boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
              }}
            >
              <Table sx={{ minWidth: 1000 }} aria-label="room prices table">
                <TableHead>
                  <TableRow
                    sx={{
                      background: "linear-gradient(135deg, #c62828 0%, #d32f2f 100%)",
                    }}
                  >
                    <TableCell sx={{ color: "white", fontWeight: 700, fontSize: "1.1rem" }}>
                      🏨 Hotel Name
                    </TableCell>
                    <TableCell sx={{ color: "white", fontWeight: 700, fontSize: "1.1rem" }}>
                      ⭐ Star Rating
                    </TableCell>
                    <TableCell sx={{ color: "white", fontWeight: 700, fontSize: "1.1rem" }}>
                      Single
                    </TableCell>
                    <TableCell sx={{ color: "white", fontWeight: 700, fontSize: "1.1rem" }}>
                      Double
                    </TableCell>
                    <TableCell sx={{ color: "white", fontWeight: 700, fontSize: "1.1rem" }}>
                      Triple
                    </TableCell>
                    {/* ✅ ACTIONS COLUMN */}
                    <TableCell sx={{ color: "white", fontWeight: 700, fontSize: "1.1rem" }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {roomPrices.map((hotel, index) => (
                    <StyledTableRow key={index}>
                      <TableCell sx={{ fontWeight: 600, fontSize: "1rem" }}>
                        {hotel.hotel_name}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={starRatingConfig[hotel.star_rating]?.label || "N/A"}
                          size="small"
                          sx={{
                            backgroundColor:
                              starRatingConfig[hotel.star_rating]?.bg || "#e0e0e0",
                            color: starRatingConfig[hotel.star_rating]?.color || "#666",
                            fontWeight: 600,
                            fontSize: "0.85rem",
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 500, color: "#2e7d32" }}>
                        ${hotel.rooms?.Single?.base_price?.toFixed(2) || "N/A"}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 500, color: "#1976d2" }}>
                        ${hotel.rooms?.Double?.base_price?.toFixed(2) || "N/A"}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 500, color: "#c62828" }}>
                        ${hotel.rooms?.Triple?.base_price?.toFixed(2) || "N/A"}
                      </TableCell>
                      {/* ✅ EDIT/DELETE BUTTONS */}
                      <TableCell sx={{ py: 1 }}>
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => handleEdit(hotel, hotel.room_id)}
                            sx={{
                              background: "#1976d2",
                              "&:hover": { background: "#1565c0" },
                              textTransform: "none",
                              fontSize: "0.8rem",
                            }}
                          >
                             Edit
                          </Button>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => handleDelete(hotel.room_id, hotel.hotel_name)}
                            sx={{
                              background: "#d32f2f",
                              "&:hover": { background: "#b71c1c" },
                              textTransform: "none",
                              fontSize: "0.8rem",
                            }}
                          >
                            Delete
                          </Button>
                        </Box>
                      </TableCell>
                    </StyledTableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* ✅ EDIT MODAL */}
          <Dialog open={editModal} onClose={() => setEditModal(false)} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ background: "#c62828", color: "white", fontWeight: 700 }}>
              Edit Hotel Room Prices
            </DialogTitle>
            <form onSubmit={handleUpdate}>
              <DialogContent sx={{ p: 3 }}>
                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mb: 2 }}>
                  <TextField
                    label="Hotel Name"
                    value={editForm.hotel_name}
                    onChange={(e) =>
                      setEditForm({ ...editForm, hotel_name: e.target.value })
                    }
                    fullWidth
                    required
                  />
                  <TextField
                    select
                    label="Star Rating"
                    value={editForm.star_rating}
                    onChange={(e) =>
                      setEditForm({ ...editForm, star_rating: e.target.value })
                    }
                    fullWidth
                    required
                  >
                    <MenuItem value="five_star">⭐⭐⭐⭐⭐ Five Star</MenuItem>
                    <MenuItem value="four_star">⭐⭐⭐⭐ Four Star</MenuItem>
                    <MenuItem value="three_star">⭐⭐⭐ Three Star</MenuItem>
                  </TextField>
                </Box>
                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 2 }}>
                  <TextField
                    label="Single Room ($)"
                    type="number"
                    value={editForm.single_base_price}
                    onChange={(e) =>
                      setEditForm({ ...editForm, single_base_price: e.target.value })
                    }
                    fullWidth
                    required
                    inputProps={{ step: "0.01" }}
                  />
                  <TextField
                    label="Double Room ($)"
                    type="number"
                    value={editForm.double_base_price}
                    onChange={(e) =>
                      setEditForm({ ...editForm, double_base_price: e.target.value })
                    }
                    fullWidth
                    required
                    inputProps={{ step: "0.01" }}
                  />
                  <TextField
                    label="Triple Room ($)"
                    type="number"
                    value={editForm.triple_base_price}
                    onChange={(e) =>
                      setEditForm({ ...editForm, triple_base_price: e.target.value })
                    }
                    fullWidth
                    required
                    inputProps={{ step: "0.01" }}
                  />
                </Box>
              </DialogContent>
              <DialogActions sx={{ p: 3 }}>
                <Button onClick={() => setEditModal(false)} variant="outlined">
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  sx={{ background: "#1976d2", color: "white" }}
                >
                  {loading ? "Updating..." : "Update Prices"}
                </Button>
              </DialogActions>
            </form>
          </Dialog>
        </CardContent>
      </StyledCard>
    </Box>
  );
};

export default RoomPriceList;